import Cookies from 'js-cookie';
const USERS_API = 'https://system.sales-management.cloud/api/users.php';

/**
 * ユーザー一覧を取得
 * @param {Object} options - オプション
 * @param {string} options.search - 検索キーワード
 * @param {string} options.role - ロール (superadmin/admin/view_admin/contractor/user)
 * @param {string} options.status - ステータス (active/inactive/suspended)
 * @param {number} options.team_id - チームID
 * @param {string} options.department - 部署
 * @param {number} options.user_id - ユーザーID
 * @param {number} options.limit - 取得件数 (デフォルト: 20, 最大: 100)
 * @param {number} options.offset - オフセット (デフォルト: 0)
 * @param {string} options.sort_by - ソート対象 (name/id/created_at/updated_at)
 * @param {string} options.sort_order - ソート順序 (asc/desc)
 * @param {boolean} options.include_team - チーム情報を含める
 * @param {boolean} options.include_permissions - ユーザー権限を含める
 * @returns {Promise<Object>} APIレスポンス
 */
export const fetchUsers = async (options = {}) => {
  try {
    const token = Cookies.get('auth_token');
    if (!token) {
      throw new Error('認証情報が見つかりません');
    }

    const params = new URLSearchParams();

    // パラメータを追加
    if (options.search) params.append('search', options.search);
    if (options.role) params.append('role', options.role);
    if (options.status) params.append('status', options.status);
    if (options.team_id) params.append('team_id', options.team_id);
    if (options.department) params.append('department', options.department);
    if (options.user_id) params.append('user_id', options.user_id);
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.sort_by) params.append('sort_by', options.sort_by);
    if (options.sort_order) params.append('sort_order', options.sort_order);
    if ('include_team' in options) params.append('include_team', options.include_team ? '1' : '0');
    if ('include_permissions' in options) params.append('include_permissions', options.include_permissions ? '1' : '0');

    const url = params.toString() ? `${USERS_API}?${params.toString()}` : USERS_API;

    console.log('ユーザーAPI リクエスト:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    

    console.log('ユーザーAPI レスポンス:', data);

    if (data.status === 'error') {
      throw new Error(data.message || 'ユーザー取得に失敗しました');
    }

    if (!response.ok) {
      throw new Error(`HTTPエラー: ${response.status}`);
    }

    return data;
  } catch (err) {
    console.error('ユーザー取得エラー:', err);
    throw err;
  }
};

/**
 * 特定のステータスのユーザー一覧を取得
 * @param {string} status - ステータス (active/inactive/suspended)
 * @param {number} limit - 取得件数
 * @returns {Promise<Array>} ユーザー配列
 */
export const fetchUsersByStatus = async (status = 'active', limit = 100) => {
  try {
    console.log(`ユーザー取得開始: status=${status}, limit=${limit}`);
    const data = await fetchUsers({
      status,
      limit,
      sort_by: 'name',
      sort_order: 'asc'
    });
    console.log('取得したユーザー数:', data.data?.users?.length || 0);
    return data.data?.users || [];
  } catch (err) {
    console.error('ステータスによるユーザー取得エラー:', err.message);
    // エラーを再スローするのではなく、空配列を返す（既存の動作を維持）
    return [];
  }
};

/**
 * ロールで絞ったユーザー一覧を取得
 * @param {string} role - ロール
 * @param {number} limit - 取得件数
 * @returns {Promise<Array>} ユーザー配列
 */
export const fetchUsersByRole = async (role, limit = 100) => {
  try {
    const data = await fetchUsers({
      role,
      status: 'active',
      limit,
      sort_by: 'name',
      sort_order: 'asc'
    });
    return data.data?.users || [];
  } catch (err) {
    console.error('ロールによるユーザー取得エラー:', err);
    return [];
  }
};

/**
 * キーワードでユーザーを検索
 * @param {string} keyword - 検索キーワード
 * @param {number} limit - 取得件数
 * @returns {Promise<Array>} ユーザー配列
 */
export const searchUsers = async (keyword, limit = 50) => {
  try {
    const data = await fetchUsers({
      search: keyword,
      status: 'active',
      limit,
      sort_by: 'name',
      sort_order: 'asc'
    });
    return data.data?.users || [];
  } catch (err) {
    console.error('ユーザー検索エラー:', err);
    return [];
  }
};

/**
 * 部署のユーザー一覧を取得
 * @param {string} department - 部署名
 * @param {number} limit - 取得件数
 * @returns {Promise<Array>} ユーザー配列
 */
export const fetchUsersByDepartment = async (department, limit = 100) => {
  try {
    const data = await fetchUsers({
      department,
      status: 'active',
      limit,
      sort_by: 'name',
      sort_order: 'asc'
    });
    return data.data?.users || [];
  } catch (err) {
    console.error('部署によるユーザー取得エラー:', err);
    return [];
  }
};
